import { inject, Injectable, Signal } from '@angular/core';
import { COOKIES_USER_SESSION_ID_KEY } from '@lentau/auth/shared/cookie.const';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { CookieFacade } from '@lentau/ui-kit/core/services/cookie/cookie.facade';
import { SSR_USER_SESSION_ID } from '../shared/constants';
import { AuthFacade } from '@lentau/auth/store';
import { EnvironmentBrand } from '@lentau/ui-kit/core/environments/environments.interface';
import { ENVIRONMENT_BRAND } from '@lentau/ui-kit/core/tokens/environment.brand';
import { UserFacade } from '@lentau/profile/store/user/user.facade';
import { IS_B2B } from '@lentau/profile/organizations/legal-entity/shared/tokens/is-b2b-profile.token';
import { getUniqueId } from '@lentau/ui-kit/core/utils/id.utils';
import { COOKIES_TOKEN_SESSION_KEY } from '@lentau/auth/shared';
import { AppStateCacheService } from '@lentau/core/app-state-cache/shared/app-state-cache.service';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';
import { generateGuid } from '@lentau/core/api/shared/guid';
import { DateUtils } from '@lentau/ui-kit/core/utils/date.utils';

@Injectable({ providedIn: 'root' })
export class ApiDataService {
  private browserApi: BrowserApiSupportService = inject(BrowserApiSupportService);
  private cookieService: CookieFacade = inject(CookieFacade);
  private authFacade: AuthFacade = inject(AuthFacade);
  private userFacade: UserFacade = inject(UserFacade);
  private appStateCacheService: AppStateCacheService = inject(AppStateCacheService);
  private isB2B: Signal<boolean> = inject(IS_B2B);
  private window: Window = inject(WINDOW);
  private readonly environmentBrand: EnvironmentBrand = inject(ENVIRONMENT_BRAND);

  public getSessionToken(): string {
    if (this.browserApi.isSearchBot()) {
      return this.environmentBrand.SSR_SESSION_TOKEN;
    }

    if (this.browserApi.isServerAndClient()) {
      return this.appStateCacheService.get(COOKIES_TOKEN_SESSION_KEY) || this.environmentBrand.SSR_SESSION_TOKEN;
    }

    return this.authFacade.sessionToken() || this.cookieService.get(COOKIES_TOKEN_SESSION_KEY) || '';
  }

  public getXOrganizationId(): string {
    const legalEntityId: string = this.userFacade.legalEntityId();

    if (this.isB2B()) {
      return legalEntityId;
    }

    return '';
  }

  public getRequestId(method: string): string {
    return `${method}_${getUniqueId()}`;
  }

  public getXPassportId(): string {
    const passportId: string = this.userFacade.passportId();

    return passportId || '';
  }

  public getUserSessionId(): string {
    if (this.browserApi.isBrowser()) {
      const sessionId: string = this.cookieService.get(COOKIES_USER_SESSION_ID_KEY);

      if (sessionId?.length) {
        return sessionId;
      } else {
        const newSessionId: string = generateGuid();
        this.cookieService.set(COOKIES_USER_SESSION_ID_KEY, newSessionId, {
          path: '/',
          domain: this.window.location.hostname.split('.').slice(-2).join('.'),
          expires: DateUtils.addDays(new Date(), 365),
        });
        return newSessionId;
      }
    }

    if (this.browserApi.isServerAndClient()) {
      return this.appStateCacheService.get(COOKIES_USER_SESSION_ID_KEY) || SSR_USER_SESSION_ID;
    }

    // Для сервера и поискового робота
    return SSR_USER_SESSION_ID;
  }
}
